/** Page heroes — interactive particle network (particles.js-style), MWWE palette. */

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
};

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  const n = Number.parseInt(value, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function cssHex(varName: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return raw || fallback;
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function initOneHeroCanvas(canvas: HTMLCanvasElement): void {
  const wrap = canvas.closest(".hero-canvas-wrap");
  const hero = canvas.closest(".hero-canvas-host");

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    wrap?.classList.add("is-static");
    return;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx || !wrap || !hero) return;

  const dotHex = cssHex("--color-mwwe-light-blue", "#b8d8d8");
  const lineHex = cssHex("--color-mwwe-steel", "#7a9e9f");
  const glowHex = cssHex("--color-mwwe-coral", "#fe5f55");

  let width = 0;
  let height = 0;
  let particles: Particle[] = [];
  let frameId = 0;
  let pointer = { x: -9999, y: -9999, active: false };

  const countForSize = (): number =>
    Math.max(48, Math.min(110, Math.floor((width * height) / 14_000)));

  const spawnParticles = (count: number): void => {
    particles = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.4 + 0.8,
    }));
  };

  const resize = (): void => {
    const rect = wrap.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    spawnParticles(countForSize());
  };

  const setPointer = (clientX: number, clientY: number, active: boolean): void => {
    const rect = wrap.getBoundingClientRect();
    pointer = {
      x: clientX - rect.left,
      y: clientY - rect.top,
      active,
    };
  };

  const tick = (): void => {
    const linkDistance = Math.min(150, width * 0.18);
    const mouseRadius = Math.min(200, width * 0.22);
    const mousePull = 0.065;

    ctx.clearRect(0, 0, width, height);

    for (const p of particles) {
      if (pointer.active) {
        const dx = pointer.x - p.x;
        const dy = pointer.y - p.y;
        const dist = Math.hypot(dx, dy);
        if (dist > 0 && dist < mouseRadius) {
          const t = (mouseRadius - dist) / mouseRadius;
          p.vx += (dx / dist) * t * mousePull;
          p.vy += (dy / dist) * t * mousePull;
        }
      }

      p.vx += (Math.random() - 0.5) * 0.015;
      p.vy += (Math.random() - 0.5) * 0.015;
      p.vx *= 0.985;
      p.vy *= 0.985;
      p.x += p.vx;
      p.y += p.vy;

      if (p.x < 0) p.x = width;
      if (p.x > width) p.x = 0;
      if (p.y < 0) p.y = height;
      if (p.y > height) p.y = 0;
    }

    for (let i = 0; i < particles.length; i += 1) {
      for (let j = i + 1; j < particles.length; j += 1) {
        const a = particles[i];
        const b = particles[j];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (dist >= linkDistance) continue;
        const alpha = (1 - dist / linkDistance) * 0.35;
        ctx.strokeStyle = rgba(lineHex, alpha);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }
    }

    ctx.fillStyle = rgba(dotHex, 0.75);
    for (const p of particles) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }

    if (pointer.active) {
      const gradient = ctx.createRadialGradient(
        pointer.x,
        pointer.y,
        0,
        pointer.x,
        pointer.y,
        mouseRadius * 0.85
      );
      gradient.addColorStop(0, rgba(glowHex, 0.14));
      gradient.addColorStop(1, rgba(glowHex, 0));
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    }

    frameId = window.requestAnimationFrame(tick);
  };

  const onMove = (event: MouseEvent | TouchEvent): void => {
    const point = "touches" in event ? event.touches[0] : (event as MouseEvent);
    if (!point) return;
    setPointer(point.clientX, point.clientY, true);
  };

  const onLeave = (): void => {
    pointer.active = false;
  };

  const onVisibility = (): void => {
    if (document.hidden) {
      window.cancelAnimationFrame(frameId);
      return;
    }
    window.cancelAnimationFrame(frameId);
    frameId = window.requestAnimationFrame(tick);
  };

  hero.classList.add("has-canvas");
  resize();

  hero.addEventListener("mousemove", onMove, { passive: true });
  hero.addEventListener("mouseleave", onLeave);
  hero.addEventListener("touchstart", onMove, { passive: true });
  hero.addEventListener("touchmove", onMove, { passive: true });
  hero.addEventListener("touchend", onLeave);

  const ro = new ResizeObserver(resize);
  ro.observe(wrap);
  document.addEventListener("visibilitychange", onVisibility);

  frameId = window.requestAnimationFrame(tick);
}

export function initHeroCanvas(): void {
  document.querySelectorAll<HTMLCanvasElement>("[data-hero-canvas]").forEach(initOneHeroCanvas);
}
